// 📁 src/utils/audioPlayer.js
// 🔊 Audio Player - Reproduce audio base64 en el navegador

/**
 * Clase para manejar la reproducción de audio
 * Responsable de reproducir, pausar y detener audio
 */
class AudioPlayer {
  constructor() {
    this.audio = null;
    this.isPlaying = false;
  }

  /**
   * Reproduce audio desde base64
   * @param {string} audioBase64 - Audio en formato base64
   * @param {string} contentType - Tipo de contenido (ej: 'audio/mpeg')
   * @returns {Promise<void>}
   */
  async playFromBase64(audioBase64, contentType) {
    // Detener audio actual si existe
    this.stop();

    return new Promise((resolve, reject) => {
      try {
        // Crear blob desde base64
        const binaryString = atob(audioBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: contentType });
        const url = URL.createObjectURL(blob);

        // Crear elemento de audio
        this.audio = new Audio(url);
        this.isPlaying = true;

        // Eventos
        this.audio.onended = () => {
          this.isPlaying = false;
          URL.revokeObjectURL(url);
          resolve();
        };

        this.audio.onerror = (error) => {
          this.isPlaying = false;
          URL.revokeObjectURL(url);
          reject(error);
        };

        // Reproducir
        this.audio.play().catch((error) => {
          this.isPlaying = false;
          URL.revokeObjectURL(url);
          reject(error);
        });
      } catch (error) {
        this.isPlaying = false;
        reject(error);
      }
    });
  }

  /**
   * Detiene la reproducción actual
   */
  stop() {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio = null;
    }
    this.isPlaying = false;
  }

  /**
   * Pausa la reproducción
   */
  pause() {
    if (this.audio) {
      this.audio.pause();
      this.isPlaying = false;
    }
  }

  /**
   * Reanuda la reproducción
   */
  resume() {
    if (this.audio && !this.isPlaying) {
      this.audio.play();
      this.isPlaying = true;
    }
  }

  /**
   * Obtiene el estado de reproducción
   * @returns {boolean}
   */
  getIsPlaying() {
    return this.isPlaying;
  }
}

// Exportar instancia singleton
export const audioPlayer = new AudioPlayer();

export default AudioPlayer;
